'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import ConfirmationModal from '@/components/ConfirmationModal';
import { classifyLead } from '@/lib/ai/classifyLead';
import { checkSpam, getFormLoadTime, isDisposableEmail } from '@/lib/spamProtection';

interface LeadCaptureFormProps {
  pageSource?: string;
  heading?: string;
  subheading?: string;
  inquiryOptions?: { value: string; label: string }[];
  /** Optional trust signal — testimonial quote, stat, or reassurance line shown above the form */
  trustSignal?: {
    quote: string;
    attribution?: string;
  };
}

const DEFAULT_INQUIRY_OPTIONS = [
  { value: 'landlord_management', label: 'Landlord — Full Management' },
  { value: 'landlord_valuation', label: 'Landlord — Free Rental Valuation' },
  { value: 'landlord_compliance', label: 'Landlord — Compliance Advice' },
  { value: 'tenant_property', label: 'Tenant — Find a Property' },
  { value: 'tenant_rights', label: 'Tenant — Know My Rights' },
  { value: 'tenant_repair', label: 'Tenant — Report a Repair' },
  { value: 'general', label: 'General Enquiry' },
];

export default function LeadCaptureForm({
  pageSource = 'home',
  heading = 'Get a free, no-obligation response',
  subheading = 'Tell us what you need and our Chester team will be in touch within 2 hours.',
  inquiryOptions = DEFAULT_INQUIRY_OPTIONS,
  trustSignal,
}: LeadCaptureFormProps) {
  const idPrefix = `lcf-${pageSource}`;
  const nameId = `${idPrefix}-name`;
  const emailId = `${idPrefix}-email`;
  const inquiryId = `${idPrefix}-inquiry`;
  const headingId = `${idPrefix}-heading`;

  const [mounted, setMounted] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    inquiry_type: inquiryOptions[0]?.value ?? 'general',
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime, setFormLoadTime] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setMounted(true);
    setFormLoadTime(getFormLoadTime());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Spam checks
    const spamResult = checkSpam(honeypot, formLoadTime);
    if (spamResult.isSpam) {
      // Silently succeed to avoid tipping off bots
      setSubmitted(true);
      setShowModal(true);
      setSubmitting(false);
      return;
    }

    if (isDisposableEmail(form.email)) {
      setError('Please use a permanent email address so we can respond to your enquiry.');
      setSubmitting(false);
      return;
    }

    try {
      const supabase = createClient();

      // 1. Store in Supabase
      const { data: insertedLead, error: dbError } = await supabase
        .from('lead_captures')
        .insert({
          full_name: form.full_name,
          email: form.email,
          inquiry_type: form.inquiry_type,
          page_source: pageSource,
        })
        .select('id')
        .single();

      if (dbError) throw new Error(dbError.message);

      // 2. Trigger Resend emails via Edge Function
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      await fetch(`${supabaseUrl}/functions/v1/send-lead-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          inquiry_type: form.inquiry_type,
          page_source: pageSource,
        }),
      });

      // 3. Gemini lead classification (non-blocking — runs after form confirms)
      if (insertedLead?.id) {
        classifyLead(form.full_name, form.inquiry_type, pageSource).then(async (classification) => {
          if (!classification) return;
          await supabase
            .from('lead_captures')
            .update({
              priority_score: classification.priority_score,
              urgency_level: classification.urgency_level,
              property_type_flag: classification.property_type_flag,
              classification_summary: classification.classification_summary,
              classified_at: new Date().toISOString(),
            })
            .eq('id', insertedLead.id);
        });
      }

      setSubmitted(true);
      setShowModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-[#F7F6F3]" aria-labelledby={headingId}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* Left: Copy */}
          <div>
            <span className="section-eyebrow mb-4 block" aria-hidden="true">Free Enquiry</span>
            <h2
              id={headingId}
              className="font-display text-3xl md:text-4xl text-[#0F2137] tracking-tight leading-tight mb-4"
            >
              {heading}
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-8 text-[0.95rem] max-w-md">
              {subheading}
            </p>

            {/* Trust signals */}
            <ul className="space-y-3" aria-label="Why contact PDA Letting">
              {[
                { icon: '⚡', text: 'Response within 2 working hours' },
                { icon: '⚖️', text: 'Backed by PDA Law Solicitors — in-house legal advice' },
                { icon: '🏡', text: 'Chester\'s only lettings agency with in-house solicitors' },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3">
                  <span className="text-base leading-none mt-0.5" aria-hidden="true">{item.icon}</span>
                  <span className="text-[#4B5563] text-sm leading-relaxed">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Form */}
          <div>
            {/* Trust signal — testimonial or reassurance line */}
            {trustSignal && (
              <div className="mb-5 rounded-xl bg-[#1B6B5A]/8 border border-[#1B6B5A]/20 px-5 py-4 flex items-start gap-3">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="#1B6B5A"
                  className="flex-shrink-0 mt-0.5 opacity-60"
                  aria-hidden="true"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <div>
                  <p className="text-[#0F2137] text-sm leading-relaxed italic">{trustSignal.quote}</p>
                  {trustSignal.attribution && (
                    <p className="text-[#6B7280] text-xs mt-1.5 font-medium">{trustSignal.attribution}</p>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl p-8 border border-[#E0DDD4] shadow-sm">
              {!mounted ? (
                /* SSR skeleton — no form fields rendered server-side to avoid extension-caused hydration mismatches */
                <div className="space-y-4 animate-pulse" aria-hidden="true">
                  <div className="h-7 bg-[#F3F2EE] rounded w-48 mb-6" />
                  <div className="h-4 bg-[#F3F2EE] rounded w-24 mb-1.5" />
                  <div className="h-10 bg-[#F3F2EE] rounded w-full" />
                  <div className="h-4 bg-[#F3F2EE] rounded w-32 mb-1.5 mt-4" />
                  <div className="h-10 bg-[#F3F2EE] rounded w-full" />
                  <div className="h-4 bg-[#F3F2EE] rounded w-28 mb-1.5 mt-4" />
                  <div className="h-10 bg-[#F3F2EE] rounded w-full" />
                  <div className="h-12 bg-[#1B6B5A] rounded-lg w-full mt-4 opacity-60" />
                </div>
              ) : (
                <>
                  <h3 className="font-display text-xl text-[#0F2137] font-medium mb-6">
                    Send us your enquiry
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4" aria-label="Lead capture form">
                    {/* Honeypot — invisible to real users, bots fill this in */}
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                    />
                    <div>
                      <label
                        className="text-[#6B7280] text-xs font-medium tracking-wide uppercase block mb-1.5"
                        htmlFor={nameId}
                      >
                        Full Name <span className="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input
                        id={nameId}
                        type="text"
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        placeholder="Your full name"
                        required
                        className="form-input-light text-sm"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <label
                        className="text-[#6B7280] text-xs font-medium tracking-wide uppercase block mb-1.5"
                        htmlFor={emailId}
                      >
                        Email Address <span className="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input
                        id={emailId}
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        className="form-input-light text-sm"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <label
                        className="text-[#6B7280] text-xs font-medium tracking-wide uppercase block mb-1.5"
                        htmlFor={inquiryId}
                      >
                        Enquiry Type <span className="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <select
                        id={inquiryId}
                        value={form.inquiry_type}
                        onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
                        required
                        className="form-input-light text-sm appearance-none"
                        aria-required="true"
                      >
                        {inquiryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {error && (
                      <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3" role="alert">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary w-full justify-center text-sm py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
                      aria-label="Submit enquiry to PDA Letting"
                    >
                      {submitting ? 'Sending…' : 'Send Enquiry'}
                      {!submitting && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                        </svg>
                      )}
                    </button>

                    <p className="text-[#9CA3AF] text-xs text-center leading-relaxed">
                      No spam. Your details are stored securely and used only to respond to your enquiry.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        accentColor="green"
        title="Enquiry Received!"
        message={
          <>
            Thank you, <strong className="text-white">{form.full_name}</strong>. We&apos;ve received your enquiry and our Chester team will be in touch within 2 working hours.
          </>
        }
        nextSteps={[
          { icon: null, text: 'You\'ll receive a confirmation email shortly.' },
          { icon: null, text: 'A member of our team will review your enquiry.' },
          { icon: null, text: 'We\'ll respond within 2 working hours, Mon–Fri.' },
        ]}
        contactNote={
          <>
            For urgent matters, call us on{' '}
            <a href="tel:01244380380" className="text-[#4DB896] font-semibold hover:underline">01244 380 380</a>
          </>
        }
        ctaLinks={[
          { href: '/home-page', label: 'Back to Home' },
        ]}
      />
    </section>
  );
}
